import { round } from 'lodash';
import { CSSProperties } from 'react';
import { Column } from 'react-table';

import IconTooltip from '../../../ui/IconTooltip/IconTooltip';

import { Learner } from './TableSummaryReport.types';
import { renderPercentageCell } from './TableSummaryReport.utils';

export const TABLE_COLUMNS: readonly Column<Learner>[] = [
  {
    Header: 'Name',
    filter: 'fuzzyText',
    accessor: 'name',
  },
  {
    Header: 'Email',
    filter: 'fuzzyText',
    accessor: 'email',
  },
  {
    Header: () => (
      <span>
        Total % Complete
        <IconTooltip
          id="tooltip-completion-percentage"
          icon="help"
          title="Completion Percentage"
          text="This is the percentage of the total course content (currently in display) the learner has completed.
            It is a weighted average that takes into account the relative amount of content in each course."
        />
      </span>
    ),
    accessor: 'totalPercentageComplete',
    Cell: ({ value }) => {
      if (!value && value !== 0) return '-';
      return renderPercentageCell(value);
    },
    filter: (rows, id: any, filterValue) => {
      // If filterValue is 0, return rows where totalPercentageComplete is 0
      if (filterValue === '0') {
        return rows.filter((row) => row.values[id] === 0);
      }

      // If filterValue is 100, return rows where totalPercentageComplete is 100
      if (filterValue === '100') {
        return rows.filter((row) => row.values[id] === 100);
      }

      // filterValue is 'between', return rows where totalPercentageComplete is between 0 and 100
      if (filterValue === 'between') {
        return rows.filter((row) => row.values[id] > 0 && row.values[id] < 100);
      }

      return rows;
    },
    Footer: ({ rows }) => {
      const total = rows.reduce(
        (acc: any, { values }) => acc + (values.totalPercentageComplete || 0),
        0,
      );

      const average = round(total / rows.length, 2);

      return renderPercentageCell(average, true);
    },
  },
];

// ===================================
// Other
// ===================================

export const TABLE_COLUMN_ACCESSORS = {
  NAME: 'name',
  EMAIL: 'email',
  TOTAL_PERCENTAGE_COMPLETE: 'totalPercentageComplete',
};

export const TABLE_COLUMNS_TO_SORT = [
  TABLE_COLUMN_ACCESSORS.NAME,
  TABLE_COLUMN_ACCESSORS.EMAIL,
  TABLE_COLUMN_ACCESSORS.TOTAL_PERCENTAGE_COMPLETE,
];

// ===================================
// Styles
// ===================================

export const TABLE_HEADER_STYLES = (isSticky: boolean): CSSProperties => ({
  boxShadow: isSticky ? '#e8e9e8 -4px 0px 4px -4px inset' : '',
  left: isSticky ? 0 : '',
  zIndex: isSticky ? 2 : 1,
});

export const TABLE_CELL_STYLES = (isSticky: boolean, cellIndex: number): CSSProperties => ({
  background: 'white',
  boxShadow: isSticky ? '#e8e9e8 -4px 0px 4px -4px inset' : '',
  left: isSticky ? 0 : '',
  position: isSticky ? 'sticky' : 'relative',
  textAlign: cellIndex < 2 ? 'left' : 'right',
  zIndex: isSticky ? 1 : 0,
});

export const TABLE_AVERAGE_CELL_STYLES = (isSticky: boolean): CSSProperties => ({
  background: '#f2f2f2',
  bottom: 0,
  boxShadow: isSticky ? '#e8e9e8 -4px 0px 4px -4px inset' : '',
  fontSize: '0.875rem',
  fontWeight: 'bold',
  left: isSticky ? '0' : '',
  textAlign: isSticky ? 'left' : 'right',
  position: 'sticky',
  zIndex: isSticky ? 2 : 1,
});
